import { formatCurrency } from "./formatCurrency";
import moment from "moment";
import {
  APPOINTMENT_PAYMENT_STATUS,
  isEndStatus,
  isEndStatusCompleted,
  isEndStatusCancelled,
  NEW_PAYMENT_STATUS,
  REFUND_STATUS,
} from "./appointmentConstants";

// --------------------
// Status name helpers
// --------------------
function getPaymentStatusName(status) {
  switch (status) {
    case 0:
      return "In progress";
    case 1:
      return "Successful";
    case 2:
      return "Failed";
    default:
      return "Unknown";
  }
}

function getRefundStatusName(status) {
  switch (status) {
    case 0:
      return "Initiated";
    case 1:
      return "In progress";
    case 2:
      return "Successful";
    case 3:
      return "Failed";
    default:
      return "Unknown";
  }
}

// Whether the appointment's hydrated eventHistory still leaves room for a refund.
// Scope exists when the money actually collected (successful payments) exceeds what has
// already been refunded — counting any refund that is already generated, i.e. SUCCESS or
// PENDING (a FAILED refund frees the amount up again). Pending payments are NOT yet
// collected, so they don't add to the refundable base.
const REFUND_SCOPE_EPSILON = 0.005;
function hasRefundScopeFromEventHistory(eventHistory) {
  if (!Array.isArray(eventHistory) || eventHistory.length === 0) return false;

  let collected = 0;
  let alreadyRefunded = 0;
  eventHistory.forEach((event) => {
    if (event?.type === "PAYMENT") {
      const p = event.payment;
      if (p && p.status === "SUCCESS") collected += Number(p.amount) || 0;
    } else if (event?.type === "REFUND") {
      const r = event.refund;
      if (r && r.status !== "FAILED") alreadyRefunded += Number(r.amount) || 0;
    }
  });

  return collected - alreadyRefunded > REFUND_SCOPE_EPSILON;
}

// Net money still held per source, derived from the hydrated eventHistory: successful payments
// add, non-failed refunds subtract, per channel. Same accounting as hasRefundScopeFromEventHistory
// above and appointment-service's refundScopeRemaining — one rule, three call sites.
//
// This exists because appointment.paymentInfo is PRIMARY-ORDER-ONLY. A cart appointment can hold
// several paid ZO-s (app checkout + Bridge-approve top-ups), and refunds allocate newest-order
// first, so the scalar routinely misses both. Reading it understated every multi-order refund.
//
// PENDING payments are deliberately excluded: an unpaid top-up link is money never collected, so
// it is not refundable. That is what keeps this in step with the server's refund plan.
export function collectedByChannelFromEventHistory(eventHistory) {
  const totals = { wallet: 0, external: 0, total: 0 };
  if (!Array.isArray(eventHistory)) return totals;

  eventHistory.forEach((event) => {
    const isRefund = event?.type === "REFUND";
    const detail = isRefund ? event.refund : event.payment;
    if (!detail) return;

    if (isRefund) {
      if (detail.status === "FAILED") return; // a failed refund frees the money up again
    } else if (detail.status !== "SUCCESS") {
      return; // only settled payments are collected money
    }

    const sign = isRefund ? -1 : 1;
    totals.wallet += sign * (Number(detail.walletAmount) || 0);
    totals.external += sign * (Number(detail.externalAmount) || 0);
  });

  // Clamp float noise (and any over-refund) so the dialog never renders "-0" or a negative.
  totals.wallet = totals.wallet > REFUND_SCOPE_EPSILON ? totals.wallet : 0;
  totals.external =
    totals.external > REFUND_SCOPE_EPSILON ? totals.external : 0;
  totals.total = totals.wallet + totals.external;
  return totals;
}

// --------------------
// Payment / refund status pills
// Shared by the appointment summary note and the payment details card so both
// always read the same.
// --------------------
export const PAYMENT_STATUS_LABELS = {
  [APPOINTMENT_PAYMENT_STATUS.PENDING]: "Pending",
  [APPOINTMENT_PAYMENT_STATUS.INITIATED]: "Initiated",
  [APPOINTMENT_PAYMENT_STATUS.COMPLETE]: "Successful",
  [APPOINTMENT_PAYMENT_STATUS.FAILED]: "Failed",
  [APPOINTMENT_PAYMENT_STATUS.LINK_EXPIRED]: "Payment Link Expired",
  [APPOINTMENT_PAYMENT_STATUS.LINK_CANCELLED]: "Payment Link Cancelled",
  [APPOINTMENT_PAYMENT_STATUS.REFUNDED]: "Successful",
};

export function getPaymentStatusDisplay(appointment) {
  const paymentStatus = appointment?.paymentStatusResponse;
  return {
    show: paymentStatus !== APPOINTMENT_PAYMENT_STATUS.SPONSORED,
    label: PAYMENT_STATUS_LABELS[paymentStatus] ?? "",
    color:
      paymentStatus === APPOINTMENT_PAYMENT_STATUS.COMPLETE ||
      paymentStatus === APPOINTMENT_PAYMENT_STATUS.REFUNDED
        ? "#1BC47D"
        : paymentStatus === APPOINTMENT_PAYMENT_STATUS.FAILED
          ? "#E44437"
          : "#F97F0F",
  };
}

// Total refunded, summed straight off the hydrated eventHistory. Unlike
// refundInfo (primary-order-only) this covers every order the appointment was
// refunded against.
export function getRefundTotalFromEventHistory(eventHistory) {
  if (!Array.isArray(eventHistory)) return 0;
  return eventHistory.reduce(
    (total, event) =>
      event?.type === "REFUND" && event.refund
        ? total + (Number(event.refund.amount) || 0)
        : total,
    0,
  );
}

// Refund status rolled up from the eventHistory's REFUND entries: a failure
// anywhere wins, "Successful" needs every refund to have succeeded, and anything
// else (PENDING, INITIATED, …) reads as in progress.
export function getRefundStatusFromEventHistory(eventHistory) {
  const statuses = (Array.isArray(eventHistory) ? eventHistory : [])
    .filter((event) => event?.type === "REFUND" && event.refund)
    .map((event) => event.refund.status);

  if (statuses.length === 0) {
    return { show: false, label: "", color: "#F97F0F" };
  }
  if (statuses.includes("FAILED")) {
    return { show: true, label: "Failed", color: "#E44437" };
  }
  if (statuses.every((status) => status === "SUCCESS")) {
    return { show: true, label: "Successful", color: "#1BC47D" };
  }
  return { show: true, label: "In progress", color: "#F97F0F" };
}

// Derived from the applicable refund legs only. A failure anywhere wins;
// "Successful" needs every applicable leg to have succeeded; otherwise the most
// advanced remaining leg names the state.
// Statuses: 0 = initiated, 1 = in progress, 2 = successful, 3 = failed.
export function getRefundStatusDisplay(appointment) {
  // An appointment carrying a hydrated eventHistory is described entirely by it —
  // same source the payment details card renders from, so the two always agree.
  if (
    Array.isArray(appointment?.eventHistory) &&
    appointment.eventHistory.length > 0
  ) {
    return getRefundStatusFromEventHistory(appointment.eventHistory);
  }
  const statuses = [
    appointment?.refundInfo?.externalRefund,
    appointment?.refundInfo?.walletRefund,
  ]
    .filter((refundLeg) => refundLeg?.isApplicable === true)
    .map((refundLeg) => refundLeg.status);

  const display = statuses.includes(3)
    ? { label: "Failed", color: "#E44437" }
    : statuses.every((status) => status === 2)
      ? { label: "Successful", color: "#1BC47D" }
      : statuses.includes(1)
        ? { label: "In progress", color: "#F97F0F" }
        : { label: "Initiated", color: "#F97F0F" };

  return { show: statuses.length > 0, ...display };
}

// --------------------
// Unified payment + refund rows for the payment details table, oldest first.
// --------------------
const TRANSACTION_DATE_FORMAT = "DD MMM YYYY hh:mm A";

const formatTransactionDate = (value) =>
  value ? moment(value).format(TRANSACTION_DATE_FORMAT) : "-";

export function buildTransactionRows(appointment) {
  const rows = [];
  const paymentInfo = appointment?.paymentInfo;
  const refundInfo = appointment?.refundInfo;

  const wallet = paymentInfo?.walletPayment;
  const external = paymentInfo?.externalPayment;

  if (paymentInfo && external?.isApplicable) {
    rows.push({
      key: "payment-external",
      method: "Payment via Razor pay",
      amount: formatCurrency(external.amount) || "-",
      orderOrRefundId: external.externalOrderId || "-",
      paymentId: external.externalPaymentId || "-",
      timestamp: external.createdAt
        ? moment(external.createdAt).valueOf()
        : null,
      transactedAt: formatTransactionDate(external.createdAt),
      isRefund: false,
      status: getPaymentRowStatus(appointment, external, true),
    });
  }

  if (paymentInfo && wallet?.isApplicable) {
    rows.push({
      key: "payment-wallet",
      method: "Payment via Wallet",
      amount: formatCurrency(wallet.amount) || "-",
      orderOrRefundId: "-",
      paymentId: "-",
      timestamp: wallet.createdAt ? moment(wallet.createdAt).valueOf() : null,
      transactedAt: formatTransactionDate(wallet.createdAt),
      isRefund: false,
      status: getPaymentRowStatus(appointment, wallet, false),
    });
  }

  if (refundInfo) {
    // A refund leg carries its own attempt time; refundInfo.createdAt is the
    // fallback when the attempt has not been stamped yet.
    const walletRefund = refundInfo.walletRefund;
    const externalRefund = refundInfo.externalRefund;

    if (walletRefund?.isApplicable) {
      const refundedAt = walletRefund.attemptAt ?? refundInfo.createdAt;
      rows.push({
        key: "refund-wallet",
        method: "Refunded to wallet",
        amount: `+${formatCurrency(walletRefund.amount) || "-"}`,
        orderOrRefundId: "-",
        paymentId: "-",
        timestamp: refundedAt ? moment(refundedAt).valueOf() : null,
        transactedAt: formatTransactionDate(refundedAt),
        isRefund: true,
        status: getRefundStatusName(walletRefund.status),
      });
    }

    if (externalRefund?.isApplicable) {
      const refundedAt = externalRefund.attemptAt ?? refundInfo.createdAt;
      rows.push({
        key: "refund-external",
        method: "Refunded to Razor pay/Account",
        amount: `+${formatCurrency(externalRefund.amount) || "-"}`,
        orderOrRefundId: externalRefund.refundId || "-",
        paymentId: externalRefund.paymentId || "-",
        timestamp: refundedAt ? moment(refundedAt).valueOf() : null,
        transactedAt: formatTransactionDate(refundedAt),
        isRefund: true,
        status: getRefundStatusName(externalRefund.status),
      });
    }
  }

  // Oldest first; rows with no timestamp keep to the end in insertion order.
  return rows.sort(
    (a, b) => (a.timestamp ?? Infinity) - (b.timestamp ?? Infinity),
  );
}

// The payment status shown per row, keeping the pending / expired-link
// overrides the previous two-table layout applied.
function getPaymentRowStatus(appointment, paymentLeg, isExternal) {
  const statusCode = appointment?.status;
  const paymentStatusResponse = appointment?.paymentStatusResponse;
  const isAptCompleted = isEndStatusCompleted(statusCode);
  const isAptCancelled = isEndStatusCancelled(statusCode);
  const isPendingPayment =
    appointment?.paymentStatus === NEW_PAYMENT_STATUS.PENDING;
  const isPayLinkValid =
    paymentStatusResponse === APPOINTMENT_PAYMENT_STATUS.INITIATED;
  const isPayLinkExpired = [
    APPOINTMENT_PAYMENT_STATUS.FAILED,
    APPOINTMENT_PAYMENT_STATUS.LINK_EXPIRED,
    APPOINTMENT_PAYMENT_STATUS.LINK_CANCELLED,
  ].includes(paymentStatusResponse);

  let status = getPaymentStatusName(paymentLeg?.status);

  if (isExternal) {
    if (isPayLinkValid) {
      status = "In progress";
    }
    if (isPendingPayment && (isAptCancelled || isAptCompleted)) {
      if (isPayLinkValid) status = "In progress";
      if (isPayLinkExpired) status = "Unsuccessful";
    }
    return status;
  }

  if (isPendingPayment && (isPayLinkValid || isPayLinkExpired)) {
    if (isAptCancelled) status = "Released";
    if (isAptCompleted) status = "Successful";
  }
  return status;
}

// --------------------
// Derive payment display state (view model)
// --------------------
export function getPaymentDisplayState(appointment) {
  const paymentStatus = appointment?.paymentStatusResponse;
  const paymentInfo = appointment?.paymentInfo;

  const hasValidPaymentInfo =
    !!paymentInfo &&
    !!paymentInfo.orderId &&
    paymentInfo.orderId.toString().trim().length > 0;

  // When the appointment has a hydrated eventHistory, derive refund eligibility from the
  // actual money movement it records (is there still an un-refunded collected amount?).
  // Otherwise fall back to the legacy refundStatus gate.
  const hasEventHistory =
    Array.isArray(appointment?.eventHistory) &&
    appointment.eventHistory.length > 0;
  // PARTIALLY_REFUNDED deliberately passes this gate: a refund exists but the balance is
  // still refundable, so the button must stay available.
  const shouldShowRefundButton = hasEventHistory
    ? hasRefundScopeFromEventHistory(appointment.eventHistory)
    : hasValidPaymentInfo &&
      appointment.refundStatus !== REFUND_STATUS.NO_REFUND &&
      appointment.refundStatus !== REFUND_STATUS.REFUND_EXIST;

  const isSponsored = paymentStatus === APPOINTMENT_PAYMENT_STATUS.SPONSORED;
  const isPending = paymentStatus === APPOINTMENT_PAYMENT_STATUS.PENDING;
  const isInitiated = paymentStatus === APPOINTMENT_PAYMENT_STATUS.INITIATED;
  const isFailed =
    paymentStatus === APPOINTMENT_PAYMENT_STATUS.FAILED ||
    paymentStatus === APPOINTMENT_PAYMENT_STATUS.LINK_EXPIRED ||
    paymentStatus === APPOINTMENT_PAYMENT_STATUS.LINK_CANCELLED;
  const isComplete =
    paymentStatus === APPOINTMENT_PAYMENT_STATUS.COMPLETE ||
    paymentStatus === APPOINTMENT_PAYMENT_STATUS.REFUNDED;

  // Failure label text
  let failureLabel = "";
  if (paymentStatus === APPOINTMENT_PAYMENT_STATUS.FAILED)
    failureLabel = "Payment failed";
  else if (paymentStatus === APPOINTMENT_PAYMENT_STATUS.LINK_EXPIRED)
    failureLabel = "Link expired";
  else if (paymentStatus === APPOINTMENT_PAYMENT_STATUS.LINK_CANCELLED)
    failureLabel = "Link cancelled";

  // Payment date/time for completed payments
  let paymentDateTime = "-";
  if (paymentInfo?.externalPayment?.isApplicable) {
    paymentDateTime = moment(paymentInfo.externalPayment.createdAt).format(
      "DD MMM YYYY hh:mm A",
    );
  } else if (paymentInfo?.walletPayment?.isApplicable) {
    paymentDateTime = moment(paymentInfo.walletPayment.createdAt).format(
      "DD MMM YYYY hh:mm A",
    );
  }

  return {
    isSponsored,
    isPending,
    isInitiated,
    isFailed,
    isComplete:
      isComplete ||
      (hasValidPaymentInfo &&
        !isPending &&
        !isInitiated &&
        !isFailed &&
        !isSponsored),
    failureLabel,

    showGenerateLink: isPending,
    showPaymentLinkShared: isInitiated,
    showRefresh: isInitiated,
    showFailureLabel: isFailed,
    showRefundButton:
      shouldShowRefundButton &&
      hasValidPaymentInfo &&
      !isPending &&
      !isInitiated &&
      !isFailed &&
      !isSponsored,
    showOrderId: hasValidPaymentInfo && !isPending && !isSponsored,
    showTotalAmountPaid:
      hasValidPaymentInfo &&
      !isPending &&
      !isInitiated &&
      !isFailed &&
      !isSponsored,

    orderId: paymentInfo?.orderId ?? "",
    // Deliberately NOT paymentInfo.amount. Like paymentInfo.orderId beside it, that field is
    // PRIMARY-ORDER-ONLY: a cart appointment can be paid over several ZO-s (app checkout plus any
    // Bridge-approve top-up), and the scalar covers just the first, so it understated the headline
    // on every multi-order cart. appointment.price is the whole appointment's value.
    totalAmount: appointment?.price ?? "-",
    price: appointment?.price ?? "-",
    paymentDateTime,
    refundId: appointment?.refundInfo?.id ?? "",
    refundReason: appointment?.refundInfo?.reason ?? "",

    // Combined payment details card
    transactionRows: buildTransactionRows(appointment),
    paymentOrderId: appointment?.paymentOrderId ?? paymentInfo?.orderId ?? "",
    paymentAmount: paymentInfo?.amount ?? appointment?.price ?? null,
    hasRefundInfo: !!appointment?.refundInfo,
    refundGrandTotal: appointment?.refundInfo?.grandTotal ?? null,
    refundCreatedBy: appointment?.refundInfo?.createdBy ?? null,
    refundCreatedById: appointment?.refundInfo?.createdById ?? null,
    paymentStatusDisplay: getPaymentStatusDisplay(appointment),
    refundStatusDisplay: getRefundStatusDisplay(appointment),
  };
}
